import { service } from '@ember/service';

// Import Highcharts only from `esm/….src.js` files, the build @highcharts/react imports, so webapp and
// the React charts in @databox/ember-integrations share one instance; Vite minifies it for production.
// A plain 'highcharts' import is the UMD build, a second copy, and UMD modules attach to whichever copy
// last set `window._Highcharts`. tsconfig `paths` types the `.src.js` files like the main entry, whose
// types the annotations and accessibility modules extend.
import Highcharts from 'highcharts/esm/highcharts.src.js';

// Highcharts 12: modules self-register on import (no factory call); keep accessibility last
import 'highcharts/esm/modules/annotations.src.js';
import 'highcharts/esm/highcharts-more.src.js';
import 'highcharts/esm/modules/streamgraph.src.js';
import 'highcharts/esm/modules/heatmap.src.js';
import 'highcharts/esm/modules/solid-gauge.src.js';
import 'highcharts/esm/modules/accessibility.src.js';

import type { BlockThemeConfig, DChartInterface, Service } from 'webapp-types';
import { BlockVisualization, BlockVisualizationSpecial } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';

import type { AnyVisualization, CustomColorMap } from 'webapp/models-custom/block-setup';
import Serviced from 'webapp/models-custom/serviced';
import BubbleRenderer from 'webapp/d-chart/renderer/bubble';
import ForecastChartRenderer from 'webapp/d-chart/renderer/forecast-chart';
import ForecastChartSmallRenderer from 'webapp/d-chart/renderer/forecast-chart-small';
import FunnelRenderer from 'webapp/d-chart/renderer/funnel';
import GaugeRenderer from 'webapp/d-chart/renderer/gauge';
import GoalChartRenderer from 'webapp/d-chart/renderer/goal-chart';
import HeatmapRenderer from 'webapp/d-chart/renderer/heatmap';
import HorizontalBarChartRenderer from 'webapp/d-chart/renderer/horizontal-bar-chart';
// for tooltip title
import LineOrBarChartRenderer from 'webapp/d-chart/renderer/line-or-bar-chart';
import PieRenderer from 'webapp/d-chart/renderer/pie';
import PipelineRenderer from 'webapp/d-chart/renderer/pipeline';
import ProgressRenderer from 'webapp/d-chart/renderer/progress';
import RadialProgressRenderer from 'webapp/d-chart/renderer/radial-progress';
import SimpleLineChartRenderer from 'webapp/d-chart/renderer/simple-line-chart';
import SpiderRenderer from 'webapp/d-chart/renderer/spider';

// Filtering exceptions
// https://www.highcharts.com/docs/chart-concepts/labels-and-string-formatting#filtering
Highcharts.AST.allowedTags.push('line'); // for bar patterns
Highcharts.AST.allowedTags.push('use'); // for snapshot icons on forecast charts
Highcharts.AST.allowedTags.push('ui-icon'); // for icon components in legends
Highcharts.AST.allowedAttributes.push('name'); // for ui-icon name attribute
Highcharts.AST.allowedAttributes.push('size'); // for ui-icon size attribute
Highcharts.AST.allowedAttributes.push('patternTransform'); // for bar patterns
Highcharts.AST.allowedAttributes.push('style'); // for bar patterns
Highcharts.AST.allowedAttributes.push('x2'); // for gradients
Highcharts.AST.allowedAttributes.push('stop-color'); // for gradients
Highcharts.AST.allowedAttributes.push('stop-opacity'); // for gradients
Highcharts.AST.allowedAttributes.push('gradientUnits'); // for gradients
Highcharts.AST.allowedAttributes.push('title'); // for tooltip title

// global callback to apply dynamic sizing to legend icons in all charts
// ui-icon custom elements set fixed pixel sizes via inline styles, but legend icons
// need to scale dynamically based on font-size. This callback styles the shadow DOM SVG.
Highcharts.addEvent(Highcharts.Chart, 'render', function () {
	const container = this.container;
	if (!container) return;

	const applyDynamicSize = () => {
		const icons = container.querySelectorAll<HTMLElement>(
			'.dbx-hc-legend-item ui-icon.legend-checkbox',
		);

		icons.forEach((icon) => {
			// override inline styles on the host element
			// note: don't set display - CSS handles showing/hiding on/off icons
			icon.style.width = '1em';
			icon.style.height = '1em';
			icon.style.verticalAlign = 'middle';

			// style the SVG inside the shadow DOM
			const shadowRoot = icon.shadowRoot;
			if (shadowRoot) {
				const svg = shadowRoot.querySelector('svg');
				if (svg) {
					svg.style.width = '100%';
					svg.style.height = '100%';
					svg.style.display = 'block';
				}
			}
		});
	};

	setTimeout(applyDynamicSize, 100);
});

type ChartRenderer =
	| LineOrBarChartRenderer
	| PipelineRenderer
	| FunnelRenderer
	| PieRenderer
	| HeatmapRenderer
	| GaugeRenderer
	| ProgressRenderer
	| RadialProgressRenderer
	| HorizontalBarChartRenderer
	| BubbleRenderer
	| SpiderRenderer
	| SimpleLineChartRenderer
	| ForecastChartRenderer
	| GoalChartRenderer;

interface DChartOptions {
	node: HTMLElement;
	maxWidth: number;
	maxHeight: number;
	visualization: AnyVisualization;
	isAnimated: boolean;
	themeData: BlockThemeConfig.BlockParsed;
	sizeModifier: number; // font size
	canAnnotate: boolean;
	annotationContext: DChartInterface.AnnotationContext;
	customColorClassMap?: CustomColorMap;
}

// This class serves as handler of Renderers (d-chart/renderer/*) e.g. swapping them on visualization change
// and also is the one that updates Highcharts.Chart instance with options (on data, size or theme change)
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q

export default class DChart extends Serviced {
	@service('chart-render-queue') declare chartRenderQueueService: Service.ChartRenderQueue;
	@service('navigation') declare navigationService: Service.Navigation;

	node: HTMLElement;
	maxWidth: number;
	maxHeight: number;
	visualization: AnyVisualization;
	isAnimated: boolean;
	themeData: BlockThemeConfig.BlockParsed;
	sizeModifier: number;
	canAnnotate: boolean;
	annotationContext: DChartInterface.AnnotationContext;
	customColorClassMap: CustomColorMap; // user-set custom color variables - need for tooltip

	// pane: a Highcharts internal of polar / gauge charts, read by the radial progress renderer
	hChart: (Highcharts.Chart & { pane?: { background?: unknown[] }[] }) | null = null;
	renderer: ChartRenderer;

	constructor(options: DChartOptions, forceSyncCreation = false) {
		super();

		// the following options are also accessed from dchart/renderer/* classes
		this.node = options.node; // .bc__chart
		this.maxWidth = options.maxWidth;
		this.maxHeight = options.maxHeight; // can be smaller
		this.visualization = options.visualization;
		this.isAnimated = options.isAnimated;
		this.themeData = options.themeData;
		this.sizeModifier = options.sizeModifier;
		this.canAnnotate = options.canAnnotate;
		this.annotationContext = options.annotationContext;
		this.customColorClassMap = options.customColorClassMap || {};

		this.renderer = this.initRenderer();

		// sync mode (w/o requestAnimationFrame) is used from unit tests and snapshooter (staggering causes extra timeouts there)
		const syncCreation = forceSyncCreation || this.navigationService.isLoadedFromSnapshooter;

		if (syncCreation) {
			this.syncCreateChart();
		} else {
			this.asyncCreateChart();
		}
	}

	asyncCreateChart(): void {
		this.chartRenderQueueService.add(this);
	}

	syncCreateChart(): void {
		this.hChart = this.createChart();
	}

	updateHChart(chartOptions: Highcharts.Options): void {
		// https://api.highcharts.com/class-reference/Highcharts.Chart#update
		// console.log('dChart update', chartOptions);
		this.hChart?.update(chartOptions, true, true, this.isAnimated);
	}

	updateVisualization(visualization: AnyVisualization): void {
		// when changing chart type, initialize new renderer, and do a full chart update
		if (this.visualization.visualizationType !== visualization.visualizationType) {
			this.visualization = visualization;

			if (this.renderer) {
				this.renderer.destroy();
			}

			this.renderer = this.initRenderer();
			this.hChart?.destroy();
			this.hChart = null;
			this.syncCreateChart();
		}
	}

	updateData(): void {
		this.renderer.updateData();
	}

	updateTheme(themeData: BlockThemeConfig.BlockParsed): void {
		// called from Content::Chart when themeData has changed
		if (!equal(this.themeData, themeData)) {
			this.themeData = themeData;
			this.renderer.updateTheme();
		}
	}

	updateCustomColors(customColorClassMap: CustomColorMap): void {
		// called from Content::Chart when customColorClassMap has changed
		if (!equal(this.customColorClassMap, customColorClassMap)) {
			this.customColorClassMap = customColorClassMap;
			this.renderer.updateDefs();
		}
	}

	// called from Content::Chart when size / zoom has changed
	setSize(maxWidth: number, maxHeight: number, sizeModifier: number): void {
		let sizeChanged = false;

		if (this.maxWidth !== maxWidth) {
			this.maxWidth = maxWidth;
			sizeChanged = true;
		}

		if (this.maxHeight !== maxHeight) {
			this.maxHeight = maxHeight;
			sizeChanged = true;
		}

		const modifierChanged = this.sizeModifier !== sizeModifier;
		this.sizeModifier = sizeModifier;

		if (sizeChanged) {
			this.hChart?.setSize(maxWidth, maxHeight);
		}
		if (sizeChanged || modifierChanged) {
			this.renderer.updateSize(modifierChanged);
		}
	}

	resetPointerPosition(): void {
		// reset cached pointer chart absolute position (do it after outside element move)
		const pointer = this.hChart?.pointer;
		if (pointer) {
			(pointer as Highcharts.Pointer & { chartPosition: object | null }).chartPosition = null; // a Highcharts internal
		}
	}

	private createChart(): Highcharts.Chart {
		const chartOptions = this.renderer.fullOptions;

		// baseOptions always set chart
		chartOptions.chart!.renderTo = this.node;
		chartOptions.chart!.width = this.maxWidth;
		chartOptions.chart!.height = this.maxHeight;

		// console.log('dChart create', chartOptions);
		return Highcharts.chart(chartOptions);
	}

	private initRenderer(): ChartRenderer {
		const rendererType =
			this.visualization.forceDChartRendererType || this.visualization.visualizationType;

		switch (rendererType) {
			case BlockVisualization.LINE_CHART:
			case BlockVisualization.BAR_CHART:
				return new LineOrBarChartRenderer(this);
			case BlockVisualization.PIPELINE:
				return new PipelineRenderer(this);
			case BlockVisualization.FUNNEL:
				return new FunnelRenderer(this);
			case BlockVisualization.PIE:
				return new PieRenderer(this);
			case BlockVisualization.HEATMAP:
				return new HeatmapRenderer(this);
			case BlockVisualization.GAUGE:
				return new GaugeRenderer(this);
			case BlockVisualization.PROGRESS:
				return new ProgressRenderer(this);
			case BlockVisualization.RADIAL_PROGRESS:
				return new RadialProgressRenderer(this);
			case BlockVisualization.HORIZONTAL_BAR_CHART:
				return new HorizontalBarChartRenderer(this);
			case BlockVisualization.BUBBLE:
				return new BubbleRenderer(this);
			case BlockVisualization.SPIDER:
				return new SpiderRenderer(this);
			case BlockVisualizationSpecial.METRIC_CHART:
				return new SimpleLineChartRenderer(this);
			case BlockVisualizationSpecial.FORECAST_CHART:
				return new ForecastChartRenderer(this);
			case BlockVisualizationSpecial.FORECAST_CHART_SMALL:
				return new ForecastChartSmallRenderer(this);
			case BlockVisualizationSpecial.GOAL_CHART:
				return new GoalChartRenderer(this);
			default:
				return new LineOrBarChartRenderer(this);
		}
	}

	teardown(): void {
		this.chartRenderQueueService.remove(this);
		try {
			this.hChart?.destroy();
		} catch (_e) {
			// skip
		}
		this.hChart = null;
	}
}
